import { ColumnDef } from "@tanstack/react-table";
import useDataTable from "hooks/useDataTable";
import withRouter from "Components/Common/withRouter";
import { DEFAULT_PROJECT_MARGIN_PERCENT, ProjectUI, vw_ProjectProfitability } from "models/project";
import ProjectStatus from "models/project-status";
import Branch from "models/branch";
import Client from "models/client";
import Employee from "models/employee";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { ProjectSliceState } from "slices/project-managment/projects/reducer";
import { getProjectSearchFields } from "slices/thunk";
import DataTableTemplate from "Components/templates/data-table-template";
import { formatCurrency, formatNumber } from "utils/tools";
import { ProjectProfitabilitySliceState, setQuery } from "slices/project-managment/project-profitabilty/reducer";
import { getProjectProfitability, downloadProjectProfitabilityCSV } from "slices/project-managment/project-profitabilty/thunk";
import PrimaryButton from "Components/atoms/buttons/primary-button";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar";
import SearchProjectProfitabilityForm from "Components/organisms/forms/search-project-profitability-form";
import useManual from "hooks/useManual"
import ActionButton from "Components/atoms/buttons/action-button";
import CheckboxInput from "Components/molecule/inputs/checkbox-input";
import { HelpIcon, ExpandAllIcon, CollapseAllIcon } from "Components/atoms/icons";
import TreeNameCell from "Components/atoms/tree-name-cell";
import Department from "models/department";
import { getExpandedRowModel } from "@tanstack/react-table";
import useProjectMarginTarget from "hooks/useProjectMarginTarget";
import { compareText, stripDepartmentPrefix, toDepartmentOptions } from "utils/department-options"


const DEFAULT_STATUS = 'Active'

// Table rows: projects, grouped Branch > Division > Department > Project. Group rows carry the
// sums of their projects and a groupName instead of a project.
type ProfitabilityRow = vw_ProjectProfitability & {
    groupName?: string
    subRows?: ProfitabilityRow[]
}

const SUM_FIELDS = ['aRAmount', 'aRAccrual', 'revenue', 'aPAmount', 'aPAccrual', 'inventory', 'timecard', 'expenses', 'margin'] as const

const groupRow = (key: string, name: string, children: ProfitabilityRow[]): ProfitabilityRow => {
    const row: any = { projectId: `group-${key}`, projectCode: name, groupName: name, branch: '', subRows: children }
    SUM_FIELDS.forEach(f => { row[f] = children.reduce((sum, c) => sum + Number(c[f] ?? 0), 0) })
    row.marginPCT = row.revenue ? (row.margin / Math.abs(row.revenue)) * 100 : 0
    return row
}

// Groups rows by a key, A-Z by group name
const groupBy = (rows: ProfitabilityRow[], keyOf: (r: ProfitabilityRow) => [string, string], build: (key: string, name: string, rows: ProfitabilityRow[]) => ProfitabilityRow) => {
    const groups = new Map<string, { name: string, rows: ProfitabilityRow[] }>()
    rows.forEach(r => {
        const [key, name] = keyOf(r)
        if (!groups.has(key)) groups.set(key, { name, rows: [] })
        groups.get(key)!.rows.push(r)
    })
    return Array.from(groups.entries())
        .map(([key, g]) => build(key, g.name, g.rows))
        .sort((a, b) => compareText(a.groupName ?? '', b.groupName ?? ''))
}
const PRIVILEGED_ROLE = "Admin"
const PRIVILEGED_POSITION_TYPE = "Exec-Director"
const PRIVILEGED_JOB_TITLE = "President"


const selectProjects = createSelector(
    (state: any) => state.Projects,
    ({
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers
    }: ProjectSliceState) => ({
        departments,
        projectStatus,
        branches,
        customers,
        projectManagers
    })
)

const selectProjectProfitability = createSelector(
    (state: any) => state.ProjectProfitability,
    ({
        projects,
        projectCount,
        query,
    }: ProjectProfitabilitySliceState) => ({
        projects,
        projectCount,
        query,
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({
        roles,
        jobTitle,
    }: ProfileSliceState) => ({
        roles,
        jobTitle
    })
)


const ProjectProfitabilityPage = (props: any) => {


    document.title = "Project Profitability | IbexIQ";
    const [exporting, setExporting] = useState<boolean>(false)
    const { handleOpenManual } = useManual("project-managment/project-profitability");
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(true)
    const dispatch: any = useDispatch();
    // At the top of your file
    const fiscal = new FiscalCalendar(DEFAULT_FISCAL_START);

    const {
        departments,
        projectStatus,
        branches,
        customers,
        projectManagers
    } = useSelector(selectProjects)

    const {
        projectCount,
        projects,
        query
    } = useSelector(selectProjectProfitability)

    const {
        roles,
        jobTitle
    } = useSelector(selectProfile)
    // Generate all options from FY2019 P1 → next fiscal year current period
    const fiscalPeriodOptions = useMemo(() => {
        const options: { value: string; label: string }[] = [];

        const today = new Date();
        const { fiscalYear: currentFY, period: currentPeriod } = fiscal.dateToFiscal(today);

        const startFY = 2019;
        const startPeriod = 1;
        const endFY = currentFY + 1;
        const endPeriod = currentPeriod;

        for (let fy = startFY; fy <= endFY; fy++) {
            const maxPeriod = fy === endFY ? endPeriod : 12;
            const minPeriod = 1;

            for (let p = minPeriod; p <= maxPeriod; p++) {
                options.push({
                    value: String(fy * 100 + p),           // e.g. "202403"
                    label: fiscal.fiscalToLabel(fy, p),    // e.g. "October 2023"
                });
            }
        }

        return options;
    }, []);

    const handleChangeQuery = async (
        updates: Record<string, any>
    ) => {
        const q = { ...query, ...updates };
        dispatch(setQuery(q));
    };

    const handleExportToCSV = useCallback(async () => {
        setExporting(true);
        await downloadProjectProfitabilityCSV(query);
        setExporting(false)
    }, [query])

    const initializePage = async () => {
        // if we dont have any of the search fields lets get them
        const q = { ...query }
        let s = projectStatus;

        if (
            projectStatus.length === 0 ||
            departments.length === 0
        ) {
            const res = await dispatch(getProjectSearchFields())
            s = res.projectStatus;
        }

        const defaultStatus = s.find((s: ProjectStatus) => s.description === DEFAULT_STATUS);
        if (defaultStatus) q.statusIds = [defaultStatus.id]

        // set search inputs to the previous month
        const now = new Date()
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const toFromDetault = fiscal.dateToFiscal(lastMonth);
        const defaultFiscalPeriod = toFromDetault.fiscalYear * 100 + toFromDetault.period
        q.toPeriod = defaultFiscalPeriod
        q.fromPeriod = defaultFiscalPeriod

        dispatch(setQuery(q))

        if (projects.length === 0) {
            await dispatch(getProjectProfitability(q))
        }
        setIsLoadingProjects(false)
    }


    // Load Projects + Search fields
    useEffect(() => {
        initializePage()
    }, [])

    const handleClickProject = (projectId: string) => {
        const url = `/project-managment/projects/project?projectId=${projectId}`
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const handleSearch = useCallback(async () => {
        setIsLoadingProjects(true);
        await dispatch(getProjectProfitability(query));
        setIsLoadingProjects(false)
    }, [query, getProjectProfitability, setIsLoadingProjects])

    // Margin bands of the fiscal year of the selected "To" period (current fiscal year when none)
    const marginFiscalYear = query.toPeriod ? Math.floor(Number(query.toPeriod) / 100) : undefined
    const marginTarget = useProjectMarginTarget(marginFiscalYear)
    const targetTitle = marginTarget
        ? `FY${marginTarget.fiscalYear} ideal margin. Minimum ${marginTarget.minimumMargin}%, warning from ${marginTarget.warningMargin}%${marginTarget.source === "DEFAULT" ? " (default, no FY target set)" : ""}`
        : "Loading margin target..."

    const columns = React.useMemo<ColumnDef<ProfitabilityRow>[]>(() => [
        {
            header: "Branch / Division / Department / Project",
            accessorKey: "projectCode",
            size: 320,
            cell: ({ row }) => row.original.groupName !== undefined
                ? <TreeNameCell row={row} indent={14}>{row.original.groupName}</TreeNameCell>
                : <TreeNameCell row={row} indent={14}><a
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "var(--bs-table-link-color)",
                        cursor: "pointer",
                    }}
                    onClick={() => handleClickProject(row.original.projectId)}
                >
                    {row.original.projectCode}
                </a></TreeNameCell>,
        },
        {
            header: "Branch",
            id: "branch",
            size: 150,
            accessorFn: (row) => row.branch ?? "",
            cell: ({ row }) =>
                row.original.branch,
        },
        {
            header: "AR Amount",
            id: "ar",
            size: 100,
            accessorFn: (row) => Number(row.aRAmount),
            meta: {
                getCellStyle: () => ({
                    backgroundColor: "var(--bs-secondary-bg)",
                    textAlign: "right"
                }),
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "Consulting Income + Disbursment Revenue",
                getCellTitle: () => "Consulting Income + Disbursment Revenue",
            },
            cell: ({ row }) => {
                const aRAmount = row.original.aRAmount
                return <span className={ProjectUI.currencyValueColor(aRAmount)}>{formatCurrency(aRAmount)}</span>
            }
        },
        {
            header: "AR Accrual",
            id: "arAccrual",
            size: 100,
            accessorFn: (row) => Number(row.aRAccrual),
            meta: {
                getCellStyle: () => ({
                    backgroundColor: "var(--bs-secondary-bg)",
                    textAlign: "right"
                }),
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "Accrued Revenue + Accrued Disbursment Revenue",
                getCellTitle: () => "Accrued Revenue + Accrued Disbursment Revenue",
            },
            cell: ({ row }) => {
                const aRAccrual = row.original.aRAccrual
                return <span className={ProjectUI.currencyValueColor(aRAccrual)}>{formatCurrency(aRAccrual)}</span>
            }

        },
        {
            header: "Revenue",
            id: "revenue",
            size: 100,
            accessorFn: (row) => Number(row.revenue),
            meta: {
                getCellStyle: () => ({
                    backgroundColor: "var(--bs-secondary-bg)",
                    textAlign: "right",
                }),
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "AR Amount + AR Accrual",
                getCellTitle: () => "AR Amount + AR Accrual",
            },
            cell: ({ row }) => {
                const revenue = row.original.revenue
                return <span className={ProjectUI.currencyValueColor(revenue)}>{formatCurrency(revenue)}</span>
            }

        },

        {
            header: "AP Amount",
            id: "ap",
            size: 100,
            accessorFn: (row) => Number(row.aPAmount),
            meta: {
                getCellStyle: (value: number) => {
                    return { backgroundColor: "var(--bs-warning-bg-subtle)", textAlign: 'right' }
                },
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "Non-Labor Costs",
                getCellTitle: () => "Non-Labor Costs",
            },
            cell: ({ row }) => {
                const aPAmount = row.original.aPAmount
                return <span className={ProjectUI.currencyValueColor(aPAmount)}>{formatCurrency(aPAmount)}</span>
            }

        },
        {
            header: "Timecard",
            id: "timeCard",
            size: 100,
            accessorFn: (row) => Number(row.timecard),
            meta: {
                getCellStyle: (value: number) => {
                    return { backgroundColor: "var(--bs-warning-bg-subtle)", textAlign: 'right' }
                },
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "Direct Hourly Cost",
                getCellTitle: () => "Direct Hourly Cost",
            },
            cell: ({ row }) => {
                const timecard = row.original.timecard
                return <span className={ProjectUI.currencyValueColor(timecard)}>{formatCurrency(timecard)}</span>
            }
        },
        {
            header: "Expenses",
            id: "expenses",
            size: 100,
            accessorFn: (row) => Number(row.expenses),
            meta: {
                getCellStyle: () => {
                    return { backgroundColor: "var(--bs-warning-bg-subtle)", textAlign: 'right' }
                },
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "AP Amount + Timecard",
                getCellTitle: () => "AP Amount + Timecard",
            },
            cell: ({ row }) => {
                const expenses = row.original.expenses
                return <span className={ProjectUI.currencyValueColor(expenses)}>{formatCurrency(expenses)}</span>
            }
        },
        {
            header: "Margin",
            id: "margin",
            size: 100,
            accessorFn: (row) => Number(row.margin),
            meta: {
                getCellStyle: () => {
                    return { textAlign: 'right' }
                },
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "Revenue + AP Amount + Timecard",
                getCellTitle: () => "Revenue + AP Amount + Timecard",
            },
            cell: ({ row }) => {
                const margin = row.original.margin
                return <span className={ProjectUI.marginColor(margin)}>{formatCurrency(margin)}</span>
            }
        },
        {
            header: "Target",
            size: 100,
            enableSorting: false,
            meta: {
                getCellStyle: () => {
                    return { textAlign: 'right' }
                },
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => targetTitle,
                getCellTitle: () => targetTitle,
            },
            cell: () => `${marginTarget?.idealMargin ?? DEFAULT_PROJECT_MARGIN_PERCENT}%`
        },
        {
            header: "Margin %",
            id: "marginPercent",
            size: 100,
            accessorFn: (row) => Number(row.marginPCT),
            meta: {
                getCellStyle: (value: number) => {
                    return { textAlign: 'right' }
                },
                getHeaderClassName: () => 'text-end',
                getHeaderTitle: () => "Margin / |Revenue| × 100 (0 if Revenue is 0)",
                getCellTitle: () => "Margin / |Revenue| × 100 (0 if Revenue is 0)",
            },
            cell: ({ row }) => {
                const marginPCT = row.original.marginPCT
                return <span className={ProjectUI.marginPercentColor(marginPCT, marginTarget)}>{formatNumber(marginPCT)}%</span>
            }
        },
    ], [marginTarget, targetTitle]);

    const handleClearSearch = useCallback(async () => {
        const q = {
            ...query,
            page: 0,
            limit: Number.MAX_SAFE_INTEGER,
            branchIds: [],
            clientIds: [],
            statusIds: [],
            fromPeriod: undefined,
            toPeriod: undefined,
            code: undefined,
            projectManagerIds: [],
            departmentIds: [],
        }
        dispatch(setQuery(q));
    }, [setQuery, query]);

    // Branch > Division (parent of the project's department) > Department > Project
    const treeRows = useMemo<ProfitabilityRow[]>(() => {
        const departmentById = new Map<string, Department>(departments.map((d: Department) => [d.id, d]))
        const byProject = (a: ProfitabilityRow, b: ProfitabilityRow) => compareText(a.projectCode ?? '', b.projectCode ?? '')
        return groupBy(projects as ProfitabilityRow[], r => [r.branchId ?? 'none', r.branch || 'No branch'], (bKey, bName, bRows) =>
            groupRow(bKey, bName, groupBy(bRows, r => {
                const division = departmentById.get(r.subAccountId)?.parentDepartment
                return [division?.id ?? 'none', division ? stripDepartmentPrefix(division.description) : 'No division']
            }, (dKey, dName, dRows) =>
                groupRow(`${bKey}-${dKey}`, dName, groupBy(dRows, r => [r.subAccountId ?? 'none', stripDepartmentPrefix(r.subAccountDescription) || 'No department'], (sKey, sName, sRows) =>
                    groupRow(`${bKey}-${dKey}-${sKey}`, sName, [...sRows].sort(byProject))
                ))
            ))
        )
    }, [projects, departments])

    const table = useDataTable({
        data: treeRows,
        columns,
        getSubRows: (row) => row.subRows,
        getExpandedRowModel: getExpandedRowModel(),
        initialState: { expanded: true },
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    });

    return <DataTableTemplate
        fullExport={handleExportToCSV}
        title={"Project Managment"}
        breadcrumbItem={"Project Profitability"}
        table={table}
        actions={
            <ActionButton
                onClick={handleOpenManual}
            >
                Help <HelpIcon />
            </ActionButton>

        }
        columns={columns}
        isLoading={isLoadingProjects}
        rowCount={projectCount}
        filters={
            <SearchProjectProfitabilityForm
                handleClearSearch={handleClearSearch}
                query={query}
                handleChangeQuery={handleChangeQuery}
                branches={branches.map((b: Branch) => ({ value: b.id, label: b.name }))}
                projectManagers={projectManagers.map((emp: Employee) => ({ value: emp.id, label: `${emp.commonName ?? emp.firstName} ${emp.lastName}` }))}
                customers={customers.map((c: Client) => ({ value: c.id, label: c.name }))}
                status={projectStatus.map((pS: ProjectStatus) => ({ value: pS.id, label: pS.description }))}
                department={toDepartmentOptions(departments)}
                fiscalPeriodOptions={fiscalPeriodOptions}
                handleSearch={handleSearch}
                isSearching={isLoadingProjects}
            />

        }
        tableActions={<>
            <ActionButton onClick={() => table.toggleAllRowsExpanded(true)}>Expand all <ExpandAllIcon /></ActionButton>
            <ActionButton onClick={() => table.toggleAllRowsExpanded(false)}>Collapse all <CollapseAllIcon /></ActionButton>
            <CheckboxInput
                label="Hide the projects with no transactions"
                checked={!!query.hideEmpty}
                onChange={(e) => handleChangeQuery({ hideEmpty: e.target.checked })}
            />
        </>}
    />

}

export default withRouter(ProjectProfitabilityPage);
